/**
 * Edit Self Memory Modal
 *
 * Two-phase modal for editing the user's self-memory:
 * Phase 1 — Warning screen explaining the impact of editing AI-maintained memory.
 * Phase 2 — Edit screen with textarea, validation error display, and force-save option.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import { useUpdateUserSelfMemory } from "../../hooks/useSpeakerMemories";
import { isValidationError } from "../../services/speakerMemories";
import type { SpeakerMemoryValidationError } from "../../types/speakerMemory";

interface EditSelfMemoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentContent: string;
  onSuccess: () => void;
}

export function EditSelfMemoryModal({
  open,
  onOpenChange,
  currentContent,
  onSuccess,
}: EditSelfMemoryModalProps) {
  const [phase, setPhase] = useState<"warning" | "edit">("warning");
  const [content, setContent] = useState(currentContent);
  const [validationError, setValidationError] =
    useState<SpeakerMemoryValidationError | null>(null);

  const updateMemory = useUpdateUserSelfMemory();

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPhase("warning");
      setContent(currentContent);
      setValidationError(null);
    }
    onOpenChange(newOpen);
  };

  const handleSave = async (force?: boolean) => {
    setValidationError(null);
    try {
      await updateMemory.mutateAsync({ content, force });
      handleOpenChange(false);
      onSuccess();
    } catch (error) {
      if (isValidationError(error)) {
        setValidationError(error);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        {phase === "warning" ? (
          <>
            <DialogHeader>
              <DialogTitle>Before you edit</DialogTitle>
            </DialogHeader>
            <div className="rounded-lg border border-amber-500 bg-amber-50 p-4 dark:bg-amber-950/30">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                This is the AI therapist&apos;s clinical observations — built
                from your actual conversations. Editing these notes can affect
                how your future sessions are interpreted. Only edit to correct
                factual errors, not to reshape perceptions.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Editing it directly will affect how the therapist interprets your
              future reflections. Only edit if the AI has something clearly
              wrong.
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={() => setPhase("edit")}>Continue to Edit</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Edit Your Memory</DialogTitle>
              <DialogDescription>
                Modify the therapist&apos;s memory about you. This will
                influence how future reflections are interpreted.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Memory content..."
              />

              {validationError && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-50 p-4 dark:bg-amber-950/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Content flagged
                      </p>
                      <ul className="list-disc pl-4 text-sm text-amber-700 dark:text-amber-300">
                        {validationError.issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                      {validationError.suggestion && (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          <strong>Suggestion:</strong>{" "}
                          {validationError.suggestion}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              {validationError ? (
                <Button
                  variant="destructive"
                  onClick={() => handleSave(true)}
                  disabled={updateMemory.isPending}
                >
                  {updateMemory.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Anyway
                </Button>
              ) : (
                <Button
                  onClick={() => handleSave()}
                  disabled={
                    updateMemory.isPending || content === currentContent
                  }
                >
                  {updateMemory.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
